import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { catchError, forkJoin, of } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import {
    FilterItem,
    FilterItemType,
} from '../../../../projects/kiene-core/src/lib/services/backend-api/classes/filter-item';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { ConfirmDialogInput } from '../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog.component';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { ConfirmDeleteDialogComponent } from '../../dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import { FileUploadService } from '../../dialogs/file-upload-dialog/file-upload.service';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import {
    KieneBooleanTableColumn,
    NumberTableColumn,
    PdfTableColumn,
    TableColumn,
} from '../../components/kiene-table/table-column';
import { TableDescriptor, ToolbarButton } from '../../components/kiene-table/table-descriptor';
import { Condition, Operator, TableOption } from '../../components/kiene-table/table-option';
import { GemeinkostenBulkUploadDialogComponent } from '../../wa-wi/gemeinkosten/gemeinkosten-bulk-upload-dialog/gemeinkosten-bulk-upload-dialog.component';
import { SonstigerErloes } from './sonstiger-erloes';
import { SonstigerErloesDialogComponent } from './sonstiger-erloes-dialog/sonstiger-erloes-dialog.component';
import { SonstigerErloesInvoiceKiService } from './sonstiger-erloes-invoice-review/sonstiger-erloes-invoice-ki.service';
import { SonstigerErloesInvoiceReviewDialogComponent } from './sonstiger-erloes-invoice-review/sonstiger-erloes-invoice-review-dialog.component';

@Component({
    selector: 'kiene-sonstige-erloese',
    templateUrl: './sonstige-erloese.component.html',
})
export class SonstigeErloeseComponent implements OnInit {
    @ViewChild('invoiceKiFileInput') invoiceKiFileInput: ElementRef<HTMLInputElement>;
    tableDescriptor: TableDescriptor = new TableDescriptor();

    constructor(
        private dialog: MatDialog,
        private api: KieneTableService,
        private messageService: MessageService,
        private localStorageService: LocalStorageService,
        private pdfService: PdfViewerDesktopService,
        private invoiceKi: SonstigerErloesInvoiceKiService,
        private fileUploadService: FileUploadService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
    ) {}

    ngOnInit(): void {
        this.tableDescriptor.headline = 'Sonstige Erlöse';
        this.tableDescriptor.uniqueIdentifier = 'SonstigeErloese';
        this.tableDescriptor.apiUrl = this.apiBaseUrl + 'sonstiger_erloes/';
        this.tableDescriptor.route = '/wawi/bestellung-extern/sonstige-erloese/';
        this.tableDescriptor.nameOfIdField = 'sonstiger_erloes_id';
        this.tableDescriptor.fileUploadApi = 'sonstiger_erloes/update_pdf.php';
        this.tableDescriptor.searchPlaceholder = 'Belegnr, Partner, Kommentar';
        this.tableDescriptor.createAllowed = true;
        this.tableDescriptor.standardCreate = false;
        this.tableDescriptor.addVoid = this.openDialog.bind(this);
        this.tableDescriptor.showDetails = false;
        this.tableDescriptor.showDetailsVoid = (e: SonstigerErloes) => {
            if (e?.primanota_id) {
                return;
            }
            if (e?.scan_review > 0 || e?.invoice_draft_id) {
                this.openScanReviewDialog(e, e?.scan_review > 0 ? 'review' : 'edit');
                return;
            }
            this.openDialog(e);
        };
        this.tableDescriptor.permissionView = 17221;
        this.tableDescriptor.permissionCreate = 17222;
        this.tableDescriptor.initialSortColumn = 'datum';
        this.tableDescriptor.initialSortDirection = 'desc';

        const cols: TableColumn[] = [];
        cols.push(new TableColumn('datum', 'Datum', 'date'));
        if (this.localStorageService.currentUserHasPermissions([17221])) {
            cols.push(new PdfTableColumn('pdf_id', 'PDF', this.showPdf.bind(this)));
        }
        cols.push(new TableColumn('belegnr', 'Belegnr'));
        cols.push(new TableColumn('partner', 'Partner'));
        cols.push(new TableColumn('partner_art', 'Art'));
        cols.push(new NumberTableColumn('betrag_brutto', 'Brutto', '1.2-2'));
        cols.push(new NumberTableColumn('betrag_offen', 'Offen', '1.2-2'));
        cols.push(
            new KieneBooleanTableColumn('scan_review', 'Scan', {
                clickable: {
                    void_: this.toggleScanReview.bind(this),
                    permissionId: 17222,
                    enabled: [new Condition('primanota_id', Operator.EXISTIERT_NICHT, null)],
                },
            }),
        );
        cols.push(new KieneBooleanTableColumn('freigegeben', 'Freigegeben'));
        cols.push(new KieneBooleanTableColumn('primanota_id', 'Journal', { tooltipFieldname: 'primanota_id' }));
        this.tableDescriptor.columns = cols;

        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(this.openInvoiceKiFilePicker.bind(this), 'document_scanner', 17002, 'Erlös scannen'),
        );
        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(
                this.openInvoiceKiBulkPicker.bind(this),
                'library_add',
                17002,
                'Erlöse Bulk (Scan-Warteschlange)',
            ),
        );
        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(this.showKiBulkErrors.bind(this), 'error', 17002, 'Scan-Bulk Fehler'),
        );

        this.fileUploadService.watchFileUploaded().subscribe(result => {
            if (result) {
                this.api.reload();
            }
        });

        this.tableDescriptor.options.push(
            new TableOption(this.openScanReviewDialog.bind(this), 'Review', 'rate_review', 17222, {
                conditions: [new Condition('scan_review', Operator.GROESSER, 0)],
            }),
        );
        this.tableDescriptor.options.push(
            new TableOption(this.openScanReviewDialog.bind(this), 'Nochmal reviewen', 'replay', 17222, {
                conditions: [
                    new Condition('scan_review', Operator.KLEINER_GLEICH, 0),
                    new Condition('invoice_draft_id', Operator.GROESSER, 0),
                ],
            }),
        );
        this.tableDescriptor.options.push(
            new TableOption(this.openDialog.bind(this), 'Editieren', 'edit', 17222, {
                conditions: [
                    new Condition('primanota_id', Operator.EXISTIERT_NICHT, null),
                    new Condition('scan_review', Operator.KLEINER_GLEICH, 0),
                ],
            }),
        );
        this.tableDescriptor.options.push(
            new TableOption(this.confirmAndDelete.bind(this), 'Löschen', 'delete', 17224, {
                conditions: [new Condition('primanota_id', Operator.EXISTIERT_NICHT, null)],
            }),
        );

        this.tableDescriptor.filterItems.push(new FilterItem('Offen', '1', FilterItemType.CHECKBOX, 'status', false));
        this.tableDescriptor.filterItems.push(
            new FilterItem('Ausgeglichen', '2', FilterItemType.CHECKBOX, 'status', false),
        );
        this.tableDescriptor.filterItems.push(
            new FilterItem('Scan-Review', '1', FilterItemType.CHECKBOX, 'scan_review', false),
        );
    }

    showPdf(e: SonstigerErloes) {
        const input = new PdfViewerDialogInput(
            'sonstiger_erloes/read_pdf.php',
            new HttpParams().set('sonstiger_erloes_id', e?.sonstiger_erloes_id?.toString()),
            'Sonstiger Erlös ' + (e.belegnr || e.partner || ''),
        );
        this.pdfService.openPdfViewer(input);
    }

    openInvoiceKiFilePicker() {
        const input = this.invoiceKiFileInput?.nativeElement;
        if (!input) {
            return;
        }
        input.value = '';
        input.click();
    }

    openInvoiceKiBulkPicker() {
        const dialogRef = this.dialog.open(GemeinkostenBulkUploadDialogComponent, {
            width: '560px',
            maxWidth: '95vw',
            autoFocus: false,
        });
        dialogRef.afterClosed().subscribe((files: File[] | undefined) => {
            if (files?.length) {
                this.startInvoiceKiBulk(files);
            }
        });
    }

    onInvoiceKiFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const selected = input.files ? Array.from(input.files) : [];
        input.value = '';
        if (selected.length === 0) {
            return;
        }
        if (selected.length === 1) {
            this.startInvoiceKi(selected[0]);
            return;
        }
        this.startInvoiceKiBulk(selected);
    }

    showKiBulkErrors() {
        this.invoiceKi.bulkFailed(40).subscribe({
            next: res => {
                const jobs = res.jobs ?? [];
                if (jobs.length === 0) {
                    this.messageService.infoMessage('Keine fehlgeschlagenen Scan-Bulk-Jobs.');
                    return;
                }
                const lines = jobs.map(j => `• ${j.fileName}${j.error ? ': ' + j.error : ''}`).join('\n');
                const input = new ConfirmDialogInput();
                input.headline = `Scan-Bulk Fehler (${jobs.length})`;
                input.text = lines;
                input.okButton = 'OK';
                input.showCancel = false;
                this.dialog.open(ConfirmDialogComponent, { data: input, width: '640px' });
            },
            error: err => {
                this.messageService.errorMessage('KI-Fehlerliste konnte nicht geladen werden.', err);
            },
        });
    }

    private startInvoiceKi(file: File) {
        this.messageService.infoMessage('Beleg wird analysiert und angelegt…');
        this.invoiceKi.preview(file).subscribe({
            next: draft => {
                const runId = draft?.runId;
                if (!runId) {
                    this.messageService.infoMessage('Keine runId vom Preview.');
                    return;
                }
                this.invoiceKi.autoCommit(runId).subscribe({
                    next: res => {
                        this.messageService.infoMessage(
                            res?.sonstigerErloesId
                                ? `Sonstiger Erlös angelegt (ID ${res.sonstigerErloesId}) – Filter „Scan-Review“.`
                                : 'Sonstiger Erlös angelegt – Filter „Scan-Review“.',
                        );
                        this.api.reload();
                    },
                    error: err => {
                        this.messageService.errorMessage('Auto-Anlage fehlgeschlagen.', err);
                    },
                });
            },
            error: err => {
                this.messageService.errorMessage('Scan-Analyse fehlgeschlagen.', err);
            },
        });
    }

    private startInvoiceKiBulk(files: File[]) {
        this.messageService.infoMessage(`${files.length} Datei(en) in Warteschlange…`);
        this.invoiceKi.bulkEnqueue(files).subscribe({
            next: res => {
                this.messageService.infoMessage(`Bulk gestartet (${res.jobCount} Dateien).`);
                this.pollBulkBatch(res.batchId);
            },
            error: err => {
                this.messageService.errorMessage('Bulk-Upload fehlgeschlagen.', err);
            },
        });
    }

    private pollBulkBatch(batchId: string) {
        const tick = () => {
            this.invoiceKi.bulkStatus(batchId).subscribe({
                next: status => {
                    const pending = (status.totals?.queued ?? 0) + (status.totals?.processing ?? 0);
                    if (pending > 0) {
                        setTimeout(tick, 3000);
                        return;
                    }
                    const ready = (status.jobs ?? []).filter(j => j.status === 'readyForReview' && !!j.runId);
                    if (ready.length === 0) {
                        this.api.reload();
                        return;
                    }
                    forkJoin(
                        ready.map(j => this.invoiceKi.autoCommit(j.runId!).pipe(catchError(() => of(null)))),
                    ).subscribe({
                        next: () => this.api.reload(),
                        error: () => this.api.reload(),
                    });
                },
                error: () => this.api.reload(),
            });
        };
        setTimeout(tick, 2000);
    }

    toggleScanReview(e: SonstigerErloes) {
        if (e.primanota_id) {
            return;
        }
        e.scan_review = e.scan_review > 0 ? 0 : 1;
        const body: any = {
            sonstiger_erloes_id: e.sonstiger_erloes_id,
            institution_id: e.institution_id || null,
            sonstiger_debitor_id: e.sonstiger_debitor_id || null,
            belegnr: e.belegnr,
            datum: e.datum,
            kommentar: e.kommentar,
            scan_review: e.scan_review,
            preise: this.parsePreise(e),
        };
        if (!body.institution_id && !body.sonstiger_debitor_id) {
            this.messageService.infoMessage('Zuerst Partner im Review setzen.');
            this.api.reload();
            return;
        }
        this.api.post(this.apiBaseUrl + 'sonstiger_erloes/update.php', body).subscribe({
            next: () => this.api.reload(),
            error: err => {
                this.messageService.errorMessage('Scan-Flag konnte nicht gesetzt werden.', err);
                this.api.reload();
            },
        });
    }

    openScanReviewDialog(e?: SonstigerErloes, mode: 'review' | 'edit' = 'review') {
        if (!e || e.primanota_id) {
            return;
        }
        const runId = String(e.invoice_draft_id || '');
        const open = (draft: any, pdfFile: File | null) => {
            this.dialog
                .open(SonstigerErloesInvoiceReviewDialogComponent, {
                    width: '100vw',
                    height: '100vh',
                    maxWidth: '100vw',
                    panelClass: 'gemeinkosten-scan-review-dialog-panel',
                    data: { draft, pdfFile, erloes: e, mode },
                })
                .afterClosed()
                .subscribe(ok => {
                    if (ok) {
                        this.api.reload();
                    }
                });
        };

        const stubDraft = {
            runId: runId || '',
            mandantId: 0,
            benutzerId: 0,
            suggestedTyp: 0,
            suggestedRechnungNr: e.belegnr,
            suggestedDatum: e.datum,
            kommentar: e.kommentar,
            preise: this.parsePreise(e).map(p => ({ kostenNetto: p.kosten_netto, mwstsatz: p.mwstsatz })),
        };

        if (runId) {
            forkJoin({
                draft: this.invoiceKi.getDraft(runId).pipe(catchError(() => of(null))),
                pdf: this.invoiceKi.getPdfFile(runId).pipe(
                    catchError(() =>
                        e.pdf_id
                            ? this.invoiceKi.getErloesPdfFile(e.sonstiger_erloes_id).pipe(catchError(() => of(null)))
                            : of(null),
                    ),
                ),
            }).subscribe({
                next: ({ draft, pdf }) => open(draft ?? stubDraft, pdf),
            });
            return;
        }

        if (e.pdf_id) {
            this.invoiceKi.getErloesPdfFile(e.sonstiger_erloes_id).subscribe({
                next: pdf => open(stubDraft, pdf),
                error: () => this.openDialog(e),
            });
            return;
        }

        this.openDialog(e);
    }

    openDialog(row?: SonstigerErloes) {
        const dialogRef = this.dialog.open(SonstigerErloesDialogComponent, {
            width: '860px',
            maxHeight: '92vh',
            data: { erloes: row ? { ...row } : null },
        });
        dialogRef.afterClosed().subscribe(saved => {
            if (saved) {
                this.api.reload();
            }
        });
    }

    confirmAndDelete(row: SonstigerErloes) {
        const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
            data: {
                title: 'Sonstigen Erlös löschen',
                text: `${row.belegnr || row.partner || 'Beleg'} wirklich löschen?`,
            },
        });
        dialogRef.afterClosed().subscribe(ok => {
            if (!ok) {
                return;
            }
            this.api.deleteEntity(this.apiBaseUrl + 'sonstiger_erloes/delete.php', row).subscribe({
                next: () => {
                    this.messageService.infoMessage('Beleg gelöscht.');
                    this.api.reload();
                },
                error: err => {
                    this.messageService.errorMessage('Löschen fehlgeschlagen.', err);
                    this.api.reload();
                },
            });
        });
    }

    private parsePreise(e: SonstigerErloes) {
        let raw: any = e?.preise || e?.preise_as_json;
        if (typeof raw === 'string') {
            try {
                raw = JSON.parse(raw);
            } catch {
                raw = [];
            }
        }
        return Array.isArray(raw) ? raw : [];
    }
}
